/**
 * PostHog Analytics Integration
 * Handles PostHog initialization, event tracking, and identity management
 */

import { POSTHOG_API_KEY, POSTHOG_API_HOST } from '../../config/config.js';
import { getUTMData, getDeviceType, isDevelopment } from './utils.js';

let initialized = false;
const DISTINCT_ID_KEY = 'visualizer_posthog_distinct_id';
const ORIGINAL_ANON_ID_KEY = 'visualizer_posthog_original_anon_id';

/**
 * Initialize PostHog
 */
export function initPostHog() {
  // Skip if no API key configured or already loaded
  if (!POSTHOG_API_KEY || POSTHOG_API_KEY === 'YOUR_POSTHOG_API_KEY') {
    console.warn('[PostHog] No API key configured - skipping initialization');
    return;
  }

  if (window.posthog && initialized) {
    if (isDevelopment()) console.log('[PostHog] Already initialized');
    return;
  }

  // Load PostHog snippet
  !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);

  // Get initial context data
  const utmData = getUTMData();

  // Initialize PostHog
  posthog.init(POSTHOG_API_KEY, {
    api_host: POSTHOG_API_HOST,

    // Enable autocapture for all clicks, form submissions, page views
    autocapture: true,

    // Capture pageviews automatically
    capture_pageview: true,

    // Capture pageleave events
    capture_pageleave: true,

    // Enable session recording (if available on plan)
    disable_session_recording: false,

    // Respect Do Not Track
    respect_dnt: true,

    // Persistence
    persistence: 'localStorage+cookie',

    // Cross-subdomain tracking
    cross_subdomain_cookie: true,

    // Bootstrap with initial properties
    bootstrap: {
      distinctID: getOrCreateDistinctId()
    },

    // Loaded callback - register super properties
    loaded: function(posthog) {
      // Register properties that will be sent with every event
      posthog.register({
        // App identifier to distinguish from ShowLark landing page
        app: 'visualizer',
        // UTM parameters
        ...utmData,
        // Device info
        device_type: getDeviceType(),
        // Page context
        referrer: document.referrer || null,
        landing_page: window.location.pathname
      });

      // Store distinct ID for other analytics modules
      const distinctId = posthog.get_distinct_id();
      if (distinctId) {
        localStorage.setItem(DISTINCT_ID_KEY, distinctId);
      }

      if (isDevelopment()) console.log('[PostHog] Initialized with autocapture enabled');
    }
  });

  initialized = true;
}

/**
 * Get or create a distinct ID for the user
 * Also preserves the original anonymous ID for analytics
 * @returns {string}
 */
function getOrCreateDistinctId() {
  let distinctId = localStorage.getItem(DISTINCT_ID_KEY);
  if (!distinctId) {
    distinctId = 'anon_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
    localStorage.setItem(DISTINCT_ID_KEY, distinctId);
    // Store as original anonymous ID (never changes, even after identify)
    localStorage.setItem(ORIGINAL_ANON_ID_KEY, distinctId);
  }
  // Ensure original anon ID is set (for users who had IDs before this change)
  if (!localStorage.getItem(ORIGINAL_ANON_ID_KEY) && distinctId.startsWith('anon_')) {
    localStorage.setItem(ORIGINAL_ANON_ID_KEY, distinctId);
  }
  return distinctId;
}

/**
 * Get the current distinct ID (may be user ID after identify)
 * @returns {string|null}
 */
export function getDistinctId() {
  if (window.posthog && typeof posthog.get_distinct_id === 'function') {
    return posthog.get_distinct_id();
  }
  // Fallback to locally stored ID if PostHog not ready
  return localStorage.getItem(DISTINCT_ID_KEY) || null;
}

/**
 * Get the original anonymous distinct ID (before any identify call)
 * This ID never changes and is useful for tracking user journey from anonymous to authenticated
 * @returns {string|null}
 */
export function getOriginalAnonId() {
  return localStorage.getItem(ORIGINAL_ANON_ID_KEY) || null;
}

/**
 * Capture a custom event
 * @param {string} eventName - Name of the event
 * @param {Object} properties - Event properties
 */
export function capture(eventName, properties = {}) {
  if (!window.posthog) {
    if (isDevelopment()) {
      console.log('[PostHog] (dev) Event:', eventName, properties);
    }
    return;
  }

  posthog.capture(eventName, {
    ...properties,
    app: 'visualizer' // Always include app identifier
  });

  if (isDevelopment()) console.log('[PostHog] Event:', eventName, properties);
}

/**
 * Identify a user (call after signup/login)
 * @param {string} userId - User's unique ID
 * @param {Object} properties - User properties
 */
export function identify(userId, properties = {}) {
  if (!window.posthog) {
    console.warn('[PostHog] Not initialized, cannot identify user');
    return;
  }

  posthog.identify(userId, {
    ...properties,
    app: 'visualizer'
  });

  // Update stored distinct ID
  const newDistinctId = posthog.get_distinct_id();
  if (newDistinctId) {
    localStorage.setItem(DISTINCT_ID_KEY, newDistinctId);
  }

  if (isDevelopment()) console.log('[PostHog] Identified user');
}

/**
 * Alias an anonymous ID to a user ID (for anonymous -> authenticated transitions)
 * @param {string} userId - The new user ID
 */
export function alias(userId) {
  if (!window.posthog) return;

  posthog.alias(userId);
  if (isDevelopment()) console.log('[PostHog] Aliased user');
}

/**
 * Set user properties without changing identity
 * @param {Object} properties - Properties to set
 */
export function setUserProperties(properties) {
  if (!window.posthog) return;
  posthog.setPersonProperties({
    ...properties,
    app: 'visualizer'
  });
}

/**
 * Register super properties (sent with every event)
 * @param {Object} properties - Properties to register
 */
export function registerProperties(properties) {
  if (!window.posthog) return;
  posthog.register(properties);
}

/**
 * Reset user identity (for logout)
 * Calls posthog.reset() first so PostHog generates its own new anonymous ID,
 * then reads that ID into localStorage to keep them in sync.
 */
export function reset() {
  if (!window.posthog) return;

  posthog.reset();

  // Read PostHog's new ID so localStorage stays in sync
  const newId = posthog.get_distinct_id();
  if (newId) {
    localStorage.setItem(DISTINCT_ID_KEY, newId);
  }

  if (isDevelopment()) console.log('[PostHog] User reset');
}

/**
 * Check if PostHog is initialized
 * @returns {boolean}
 */
export function isInitialized() {
  return initialized && !!window.posthog;
}
